import { getAccessToken } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface UserIntent {
  userId: string;
  intentKey: string;
  game: 'pokemon' | 'onepiece';
  setId: string;
  cardId: string;
  isWishlist: boolean;
  isFavorite: boolean;
  wishlistQuantity: number;
  cardName: string;
  imageUrl: string;
  rarity?: string;
  color?: string[];
  types?: string[];
  cardNumber?: string;
  baseCardId?: string;
  imageId?: string;
  updatedAt: string;
}

export interface PaginatedUserIntents {
  items: UserIntent[];
  lastEvaluatedKey?: Record<string, any>;
}

async function getAuthHeaders(): Promise<HeadersInit> {
  const token = await getAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

/**
 * Get global wishlist and favorite counts
 */
export async function getGlobalIntentCounts(): Promise<{ wishlist: number, favorite: number }> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/user/intents/counts`, { headers });
  
  if (!response.ok) {
    throw new Error('Failed to fetch intent counts');
  }
  
  return response.json();
}

/**
 * Get all intents for the user with pagination
 */
export async function getUserIntents(limit: number = 20, lastKey?: any): Promise<PaginatedUserIntents> {
  const headers = await getAuthHeaders();
  let url = `${API_BASE_URL}/api/user/intents?limit=${limit}`;
  if (lastKey) {
    url += `&lastKey=${encodeURIComponent(JSON.stringify(lastKey))}`;
  }
  
  const response = await fetch(url, { headers });
  
  if (!response.ok) {
    throw new Error('Failed to fetch user intents');
  }
  
  return response.json();
}

/**
 * Get intents for a specific set
 */
export async function getUserIntentsBySet(setId: string): Promise<UserIntent[]> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/user/intents/set/${setId}`, { headers });
  
  if (!response.ok) {
    throw new Error('Failed to fetch set intents');
  }
  
  const data = await response.json();
  return data.intents || [];
}

/**
 * Toggle wishlist status
 */
export async function toggleWishlist(params: {
  game: 'pokemon' | 'onepiece';
  setId: string;
  cardId: string;
  isWishlist: boolean;
  wishlistQuantity?: number;
  cardName: string;
  imageUrl: string;
  rarity?: string;
  color?: string[];
  types?: string[];
  cardNumber?: string;
  baseCardId?: string;
  imageId?: string;
}): Promise<UserIntent> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/user/intents/wishlist/toggle`, {
    method: 'POST',
    headers,
    body: JSON.stringify(params),
  });
  
  if (!response.ok) {
    throw new Error('Failed to toggle wishlist');
  }
  
  const data = await response.json();
  return data.intent;
}

/**
 * Toggle favorite status
 */
export async function toggleFavorite(params: {
  game: 'pokemon' | 'onepiece';
  setId: string;
  cardId: string;
  isFavorite: boolean;
  cardName: string;
  imageUrl: string;
  rarity?: string;
  color?: string[];
  types?: string[];
  cardNumber?: string;
  baseCardId?: string;
  imageId?: string;
}): Promise<UserIntent> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/user/intents/favorite/toggle`, {
    method: 'POST',
    headers,
    body: JSON.stringify(params),
  });
  
  if (!response.ok) {
    throw new Error('Failed to toggle favorite');
  }
  
  const data = await response.json();
  return data.intent;
}
